import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Table } from "@tanstack/react-table";
import { Loader2, Power, PowerOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { EnableSignalsDialog } from "@/components/admin/TradingLimits/EnableSignalsDialog";
import { updateSignalsAlgoIntradayAlgoSignalsAlgoPatch as updateSignalsAlgo } from "@/client-admin/services.gen";
import { InnovaSignal } from "@/client-admin/types.gen";

interface DataTableToolbarProps {
  table: Table<InnovaSignal>;
}

export function DataTableToolbar({ table }: DataTableToolbarProps) {
  const queryClient = useQueryClient();
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const hasBuiltSignals = selectedRows.some((row) => row.original.Algo === 2);
  const hasHeldSignals = selectedRows.some((row) => row.original.Algo === 0);
  const selectedIds = selectedRows.map((row) => row.original.Id).filter((id): id is number => id !== null);
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: updateSignalsAlgo,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("intraday-algo") });
      table.resetRowSelection();
      setEnableDialogOpen(false);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.body?.detail ?? "Failed to update signals.",
        variant: "destructive",
      });
    },
  });

  const enableSelectedSignals = () => mutation.mutate({ requestBody: { ids: selectedIds, algo: 1 } });

  // Enabling held signals runs the trading limit checks first.
  const handleEnableClick = () => {
    if (hasHeldSignals) {
      setEnableDialogOpen(true);
    } else {
      enableSelectedSignals();
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {selectedRows.length} of {table.getFilteredRowModel().rows.length} row(s) selected
      </div>
      {selectedRows.length > 0 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={mutation.isPending || hasBuiltSignals}
            title={hasBuiltSignals ? "Cannot modify signals that already have slices built (Algo=2)" : undefined}
            onClick={handleEnableClick}
          >
            {mutation.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Power className="mr-1 h-4 w-4" />
            )}
            Set Algo 1
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={mutation.isPending || hasBuiltSignals}
            title={hasBuiltSignals ? "Cannot modify signals that already have slices built (Algo=2)" : undefined}
            onClick={() => mutation.mutate({ requestBody: { ids: selectedIds, algo: 0 } })}
          >
            {mutation.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <PowerOff className="mr-1 h-4 w-4" />
            )}
            Set Algo 0
          </Button>
        </div>
      )}
      <EnableSignalsDialog
        signalIds={selectedIds}
        open={enableDialogOpen}
        onOpenChange={setEnableDialogOpen}
        onConfirm={enableSelectedSignals}
        isConfirming={mutation.isPending}
      />
    </div>
  );
}
